import { useQuery } from '@tanstack/react-query'
import { Crown, Lock, Medal } from 'lucide-react'
import { MARK } from '@sdg/shared'
import { Callout, Chip, Panel, Section } from './Section'
import { cn } from '../../lib/cn'
import { get } from '../../lib/api'
import { fmtAvg, fmtScore } from '../../lib/score'
import type { CategoryResult, Standing } from './types'

/**
 * Slide 24: results for the judged awards — three in the deck, four since the
 * Committee added Best Entrepreneurship Booth.
 * Each judge's own total is shown at 1 decimal (identical to the Judge screen
 * and the Judging grid); the final standing is their average at 2 decimals.
 */

export function ResultsSection() {
  const results = useQuery({
    queryKey: ['admin-results'],
    queryFn: () => get<{ categories: CategoryResult[] }>('/api/admin/results', 'staff'),
    refetchOnWindowFocus: true,
  })

  const categories = results.data?.categories ?? []
  const allComplete = categories.length > 0 && categories.every((c) => c.complete)

  return (
    <Section
      title="Results"
      description={`Each KPI = (mark ÷ ${MARK.max}) × weight. A judge's total is out of 100; the final score is the average of the judges.`}
    >
      <div className="space-y-3">
        {results.isLoading && (
          <p className="py-16 text-center text-sm text-slate-500">Working out the standings…</p>
        )}

        {!results.isLoading && !allComplete && (
          <Callout tone="attention">
            <b>Provisional.</b> Winners are published only once all three judges have submitted every score
            sheet. Until then these standings can still change, and any subject scored by fewer than three
            judges is marked as such.
          </Callout>
        )}

        {/*
          Four judged awards since the Committee added the entrepreneurship
          booths, so this wraps to two before it tries four across — four
          standings tables side by side truncate the booth names that are the
          only way to tell them apart.
        */}
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
          {categories.map((category) => (
            <CategoryCard key={category.rubric.code} category={category} />
          ))}
        </div>
      </div>
    </Section>
  )
}

const MEDAL = ['bg-amber-400 text-white', 'bg-slate-400 text-white', 'bg-orange-400 text-white']

function CategoryCard({ category }: { category: CategoryResult }) {
  const [first, ...rest] = category.standings
  const isStage = category.rubric.subjectType === 'performance_group'
  const list = category.complete ? rest : category.standings
  const pct = category.expectedSheets ? (category.submittedSheets / category.expectedSheets) * 100 : 0

  return (
    <Panel
      title={category.rubric.title}
      description={`${category.submittedSheets} of ${category.expectedSheets} sheets in`}
      actions={<Chip tone={category.complete ? 'positive' : 'attention'}>{category.complete ? 'Final' : 'Provisional'}</Chip>}
      bodyClassName="p-0"
    >
      <div className="h-1 bg-slate-100">
        <div className="h-full bg-orange-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>

      {/* Winner (withheld until complete) */}
      {first &&
        (category.complete ? (
          <div className="bg-gradient-to-br from-amber-50 via-white to-orange-50 px-4 py-4 sm:px-5">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-600">
              <Crown className="size-4" strokeWidth={2.4} />
              {isStage ? 'Winner' : '1st place'}
              {first.unresolvedTie && ' · tied'}
            </p>
            <p className="mt-1.5 text-lg font-extrabold leading-tight text-[#0B1E3F]">{first.label}</p>
            {first.detail && <p className="text-xs text-slate-500">{first.detail}</p>}
            <p className="mt-2 text-3xl font-black leading-none tabular-nums text-[#0B1E3F]">
              {fmtAvg(first.finalScore)}
              <span className="text-sm font-semibold text-slate-400"> / 100</span>
            </p>
            <JudgeChips standing={first} />
            {first.unresolvedTie && (
              <p className="mt-2 text-xs font-bold text-red-600">Still level after the tie-break: the panel decides.</p>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2.5 bg-slate-50 px-4 py-3.5 sm:px-5">
            <Lock className="size-4 shrink-0 text-slate-400" strokeWidth={2.2} />
            <p className="text-xs leading-snug text-slate-500">
              The winner is published once all {category.expectedSheets} sheets for this award are submitted.
            </p>
          </div>
        ))}

      {/* Standings */}
      <ul className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
        {list.map((s) => {
          const complete = s.judgesCounted === s.judgesExpected
          return (
            <li key={s.subjectKey} className="px-4 py-3 sm:px-5">
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    'grid size-8 shrink-0 place-items-center rounded-full text-xs font-black tabular-nums',
                    s.place <= 3 ? MEDAL[s.place - 1] : 'bg-slate-100 text-slate-500',
                  )}
                >
                  {s.place <= 3 ? <Medal className="size-4" strokeWidth={2.4} /> : s.place}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-[#0B1E3F]">
                    {s.label}
                    {s.unresolvedTie && <span className="text-red-600"> =</span>}
                    {/*
                      A tie the panel settled. The real score is still printed
                      beside it — this is the Committee's own table, where the
                      judges' arithmetic must stay visible even though the
                      screen separates the two numbers.
                    */}
                    {!s.unresolvedTie && s.panelRank != null && (
                      <span
                        className="font-normal text-slate-400"
                        title="Tied on score — placed by the decision of the judging panel"
                      >
                        {' '}· panel
                      </span>
                    )}
                  </span>
                  {s.detail && <span className="block truncate text-xs text-slate-500">{s.detail}</span>}
                </span>

                <span
                  className={cn(
                    'shrink-0 text-base font-black tabular-nums',
                    complete ? 'text-[#0B1E3F]' : 'text-slate-400',
                  )}
                >
                  {s.judgesCounted === 0 ? '—' : fmtAvg(s.finalScore)}
                </span>
              </div>

              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cn('h-full rounded-full', complete ? 'bg-emerald-500' : 'bg-amber-400')}
                  style={{ width: `${Math.min(Math.max(s.finalScore, 0), 100)}%` }}
                />
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <JudgeChips standing={s} />
                {!complete && (
                  <span className="text-[11px] font-semibold text-amber-600">
                    {s.judgesCounted === 0 ? 'not yet scored' : `only ${s.judgesCounted} of ${s.judgesExpected} judges in`}
                  </span>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </Panel>
  )
}

/** Each judge's own total, same 1-decimal figure the judge saw on their screen. */
function JudgeChips({ standing }: { standing: Standing }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5 first:mt-0">
      {standing.judgeTotals.map((j) => (
        <span
          key={j.judgeLabel}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold tabular-nums',
            j.submitted ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400',
          )}
        >
          <span className="font-medium">{j.judgeLabel}</span>
          {j.total != null ? fmtScore(j.total) : '—'}
        </span>
      ))}
    </div>
  )
}
